<template>
  <div class="scanner-input-v2">
    <!-- Step 1: Scan item QR -->
    <div class="scanner-section">
      <div class="scanner-card">
        <div class="scanner-card-header">
          <span class="step-number">1</span>
          <label class="scanner-label">{{ t('Escaneie o código do item') }}</label>
        </div>

        <div class="scanner-input-shell" @keyup.enter="handleManualLookup">
          <span class="scanner-indicator" :class="{ active: hasItem }"></span>
          <input-qrcode
            ref="itemQrcodeRef"
            elem-id="scanner-v2-item-input"
            v-model="itemValue"
            :label="''"
            :placeholder="t('Escaneie ou digite o QR code do item')"
            :cameraTitle="t('Escaneie o Item')"
            style="flex: 1;"
            :disabled="scannedItem !== null"
            data-testid="pdv-scanner-v2-item-input"
            @camera:open="itemCameraOpen = true"
            @camera:close="itemCameraOpen = false"
            @update:modelValue="handleItemUpdate"
          />
          <Button
            v-if="scanMode === 'manual' && hasItem && !scannedItem"
            icon="pi pi-search"
            severity="success"
            rounded
            :loading="isLookingUp"
            :aria-label="t('Buscar item')"
            data-testid="pdv-scanner-v2-lookup-button"
            @click="handleManualLookup"
          />
          <Button
            v-if="scannedItem"
            icon="pi pi-times"
            severity="secondary"
            text
            rounded
            :aria-label="t('Limpar item')"
            data-testid="pdv-scanner-v2-clear-item-button"
            @click="handleClearItem"
          />
        </div>
      </div>
    </div>

    <!-- Step 2: Loading state -->
    <div v-if="isLookingUp" class="panel-state">
      <i class="pi pi-spinner pi-spin"></i>
      <span>{{ t('Buscando informações do item...') }}</span>
    </div>

    <!-- Step 3: Item info header (shown after scan) -->
    <template v-if="scannedItem && !isLookingUp">
      <div class="item-info-card" data-testid="pdv-scanner-v2-item-info">
        <div class="item-info-top">
          <span class="item-info-service">
            <i class="pi pi-tag"></i>
            {{ scannedItem.so_order_item_service_label || t('Serviço') }}
          </span>
          <span class="item-info-type-badge">{{ t('Item') }}</span>
        </div>
        <div class="item-info-details">
          <div class="item-info-detail">
            <span class="detail-label">{{ t('Cliente') }}</span>
            <span class="detail-value">{{ scannedItem.so_order_client_name || t('—') }}</span>
          </div>
          <div class="item-info-detail">
            <span class="detail-label">{{ t('OS') }}</span>
            <span class="detail-value">#{{ scannedItem.so_order_id || '—' }}</span>
          </div>
          <div class="item-info-detail">
            <span class="detail-label">{{ t('Prazo') }}</span>
            <span class="detail-value">{{ formattedDeadline }}</span>
          </div>
          <div class="item-info-detail">
            <span class="detail-label">{{ t('Item') }}</span>
            <span class="detail-value">{{ scannedItem.so_order_item_description || scannedItem.so_order_item_service_label || t('Item #' + (scannedItem.so_order_item_id || '')) }}</span>
          </div>
        </div>
      </div>

      <!-- Step 4: Ordered steps -->
      <div class="steps-card" data-testid="pdv-scanner-v2-steps">
        <div class="steps-card-header">
          <h3>{{ t('Etapas de Produção') }}</h3>
          <span class="steps-progress">
            {{ completedStepsCount }}/{{ matchedSteps.length }} {{ t('concluídas') }}
          </span>
        </div>

        <div class="steps-list">
          <div
            v-for="(step, index) in matchedSteps"
            :key="step.so_work_order_step_id || index"
            class="step-item"
            :class="[
              'step-status-' + step.so_work_order_step_status,
              { 'step-next-expected': step.isNextExpected && !reprocessArmed },
              { 'step-selected': step.so_work_order_step_id === selectedStepId },
            ]"
            :data-testid="'pdv-scanner-v2-step-' + index"
            @click="handleStepClick(step)"
          >
            <div class="step-status-icon">
              <i v-if="step.so_work_order_step_status === 'completed'" class="pi pi-check-circle"></i>
              <i v-else-if="step.so_work_order_step_status === 'skipped'" class="pi pi-ban"></i>
              <i v-else-if="step.so_work_order_step_status === 'in_progress'" class="pi pi-spinner pi-spin"></i>
              <i v-else-if="step.so_work_order_step_status === 'reprocess_pending'" class="pi pi-exclamation-triangle"></i>
              <i v-else class="pi pi-circle"></i>
            </div>
            <div class="step-content">
              <div class="step-topline">
                <span class="step-name">{{ step.so_work_order_step_name || t('Etapa ' + (index + 1)) }}</span>
                <span
                  v-if="step.isNextExpected && !reprocessArmed"
                  class="step-next-badge"
                  data-testid="pdv-scanner-v2-next-badge"
                >
                  {{ t('PRÓXIMO') }}
                </span>
              </div>
              <span class="step-status-text">{{ stepStatusLabel(step.so_work_order_step_status) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions row -->
      <div class="scanner-actions">
        <Button
          v-if="!reprocessArmed"
          icon="pi pi-forward"
          :label="nextActionLabel"
          size="large"
          :disabled="!canAdvance || isScanning"
          :loading="isScanning"
          severity="success"
          data-testid="pdv-scanner-v2-advance-button"
          @click="handleAdvance"
        />

        <div class="scanner-actions-secondary">
          <small v-if="reprocessArmed" class="scanner-reprocess-hint">
            {{ t('Clique em "Solicitar" para abrir o reprocesso deste item.') }}
          </small>
          <Button
            v-if="reprocessArmed"
            icon="pi pi-exclamation-triangle"
            :label="t('Solicitar')"
            severity="warning"
            data-testid="pdv-scanner-v2-reprocess-button"
            @click="handleReprocessSubmit"
          />
        </div>
      </div>
    </template>

    <!-- Reprocess toggle (renders in-tree normally; teleports above camera when open) -->
    <Teleport to="body" :disabled="!itemCameraOpen">
      <div
        class="scanner-reprocess-row"
        :class="{ 'is-over-camera': itemCameraOpen }"
        data-testid="scanner-v2-reprocess-toggle"
      >
        <InputToggle
          elem-id="scanner-v2-reprocess-toggle-input"
          :label="t('Solicitar reprocesso')"
          :model-value="reprocessArmed"
          @update:modelValue="handleReprocessToggle"
        />
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOCatalog } from '../../../composables/useSOCatalog';
import { useSOProducao } from '../../../composables/useSOProducao';
import { eventBus } from '../../../eventBus';
import { log, error as logError } from '../../../utils/DebugLogger';
import type { Orders_LookupQrcodeItemRow } from '../../../contracts/Orders_LookupQrcodeItemRow';
import type { Orders_WorkOrderStepRow } from '../../../contracts/Orders_WorkOrderStepRow';
import type { ProductionSteps_StepRow } from '../../../contracts/ProductionSteps_StepRow';

interface QrcodeInputInstance {
  openCamera: () => void;
  closeCamera: () => void;
}

interface MatchedStep extends Orders_WorkOrderStepRow {
  productionStepQrcode?: string;
  isNextExpected?: boolean;
}

interface Props {
  modelValue?: string;
  /**
   * 'auto': a busca do item dispara sozinha 5 s após parar de digitar (folga
   * para digitação manual do QR — 500 ms disparava no meio da digitação).
   * 'manual': só o botão de buscar (ou Enter) aciona o gatilho.
   */
  scanMode?: 'auto' | 'manual';
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  scanMode: 'auto'
});

const emit = defineEmits<{
  'scan': [stationQr: string, itemQr: string];
  'reprocess-scan': [itemQr: string];
  'update:modelValue': [value: string];
}>();

const { t } = useI18n();
const { fetchAll } = useSOCatalog('production_steps');
const { lookupByQrcode } = useSOProducao();

const itemQrcodeRef = ref<QrcodeInputInstance | null>(null);
const itemValue = ref<string>('');
const productionSteps = ref<ProductionSteps_StepRow[]>([]);
const scannedItem = ref<Orders_LookupQrcodeItemRow | null>(null);
const scannedSteps = ref<Orders_WorkOrderStepRow[]>([]);
const selectedStepId = ref<number | null>(null);
const reprocessArmed = ref<boolean>(false);
const itemCameraOpen = ref<boolean>(false);
const isLookingUp = ref<boolean>(false);
const isScanning = ref<boolean>(false);

const hasItem = computed<boolean>(() => itemValue.value.trim().length > 0);

let lookupDebounceTimer: ReturnType<typeof setTimeout> | null = null;

const matchedSteps = computed<MatchedStep[]>(() => {
  // Build a lookup map: production_step_id → qrcode
  const stepQrcodeMap: Record<number, string> = {};
  for (const ps of productionSteps.value) {
    if (ps.so_production_step_id && ps.so_production_step_qrcode) {
      stepQrcodeMap[ps.so_production_step_id] = ps.so_production_step_qrcode;
    }
  }

  // Find the next expected step: pending, in_progress, or reprocess_pending
  let nextPendingId: number | null = null;
  const steps = scannedSteps.value;
  for (const step of steps) {
    if (['pending', 'in_progress', 'reprocess_pending'].includes(step.so_work_order_step_status)) {
      nextPendingId = step.so_work_order_step_id;
      break;
    }
  }

  return steps.map((step) => ({
    ...step,
    productionStepQrcode: stepQrcodeMap[step.so_work_order_step_production_step_id] || undefined,
    isNextExpected: step.so_work_order_step_id === nextPendingId,
  }));
});

const completedStepsCount = computed<number>(() =>
  matchedSteps.value.filter(s => s.so_work_order_step_status === 'completed').length
);

const canAdvance = computed<boolean>(() => {
  if (!selectedStepId.value && matchedSteps.value.length === 0) return false;
  if (reprocessArmed.value) return false;
  return true;
});

const formattedDeadline = computed<string>(() => {
  const val = (scannedItem.value as Record<string, unknown>)?.so_work_order_deadline_at as string | undefined;
  if (!val) return t('—');
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return val;
    return d.toLocaleDateString('pt-BR');
  } catch {
    return val;
  }
});

const nextActionLabel = computed<string>(() => {
  if (matchedSteps.value.length === 0) return t('Avançar');

  const nextStep = matchedSteps.value.find(s => s.isNextExpected);
  if (nextStep) {
    return t('Avançar') + ': ' + (nextStep.so_work_order_step_name || '');
  }

  // All steps are done or no next expected
  const allDone = matchedSteps.value.every(
    s => s.so_work_order_step_status === 'completed' || s.so_work_order_step_status === 'skipped'
  );
  if (allDone) return t('Todos os passos concluídos');

  return t('Avançar');
});

const stepIdsToStationQrcode = (step: MatchedStep): string | null => {
  return step.productionStepQrcode || null;
};

const handleItemUpdate = (value: string): void => {
  emit('update:modelValue', value);

  // Skip processing if empty or already have scanned item
  if (!value || !value.trim() || scannedItem.value) return;

  // Manual mode: only the search button (or Enter) triggers the lookup.
  if (props.scanMode === 'manual') return;

  // Debounce lookup to avoid premature API calls during manual typing.
  // The API call fires 5s after the user stops typing, so partial
  // codes typed character by character never trigger a failed lookup.
  if (lookupDebounceTimer) {
    clearTimeout(lookupDebounceTimer);
  }

  lookupDebounceTimer = setTimeout(async () => {
    const qr = itemValue.value.trim();
    if (qr.length > 0) {
      await doLookup(qr);
    }
  }, 5000);
};

const handleManualLookup = (): void => {
  if (scannedItem.value || isLookingUp.value) return;
  if (lookupDebounceTimer) {
    clearTimeout(lookupDebounceTimer);
    lookupDebounceTimer = null;
  }
  const qr = itemValue.value.trim();
  if (qr.length > 0) {
    void doLookup(qr);
  }
};

const doLookup = async (qrcode: string): Promise<void> => {
  isLookingUp.value = true;
  try {
    const result = await lookupByQrcode(qrcode) as {
      item?: Orders_LookupQrcodeItemRow;
      steps?: Orders_WorkOrderStepRow[];
    } | undefined;

    if (!result?.item) {
      eventBus.emit('globalMessage.error', t('Item não encontrado para este QR code.'));
      itemValue.value = '';
      return;
    }

    scannedItem.value = result.item;
    scannedSteps.value = result.steps || [];
    selectedStepId.value = null;

    // Auto-select the first non-completed, non-skipped step
    const nextStep = (result.steps || []).find(
      (s: Orders_WorkOrderStepRow) => !['completed', 'skipped'].includes(s.so_work_order_step_status)
    );
    if (nextStep) {
      selectedStepId.value = nextStep.so_work_order_step_id;
    }

    log('ScannerInputV2', 'doLookup', 'Item found', {
      itemId: result.item.so_order_item_id,
      stepsCount: (result.steps || []).length,
      nextStepId: selectedStepId.value,
    });
  } catch (err) {
    logError('ScannerInputV2', 'doLookup', 'Error', err);
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
    eventBus.emit('globalMessage.error', message || t('Erro ao buscar item'));
    itemValue.value = '';
    scannedItem.value = null;
    scannedSteps.value = [];
    selectedStepId.value = null;
  } finally {
    isLookingUp.value = false;
  }
};

const handleStepClick = (step: MatchedStep): void => {
  // Clicking a pending/in-progress step selects it for advancement
  if (step.so_work_order_step_status === 'completed' || step.so_work_order_step_status === 'skipped') {
    return;
  }
  selectedStepId.value = step.so_work_order_step_id;
};

const handleAdvance = async (): Promise<void> => {
  const selectedStep = matchedSteps.value.find(
    s => s.so_work_order_step_id === selectedStepId.value
  );

  // No specific step selected → find the next expected pending step
  const targetStep = selectedStep || matchedSteps.value.find(s => s.isNextExpected);

  if (!targetStep) {
    eventBus.emit('globalMessage.info', t('Todos os passos já foram concluídos.'));
    return;
  }

  const stationQr = stepIdsToStationQrcode(targetStep);
  if (!stationQr) {
    eventBus.emit('globalMessage.error', t('Etapa não possui QR code configurado.'));
    return;
  }

  const itemQr = itemValue.value.trim();
  if (!itemQr) {
    eventBus.emit('globalMessage.error', t('Item não informado.'));
    return;
  }

  log('ScannerInputV2', 'handleAdvance', 'Advancing', {
    stationQr,
    itemQr,
    stepName: targetStep.so_work_order_step_name,
  });

  isScanning.value = true;
  try {
    emit('scan', stationQr, itemQr);
  } finally {
    isScanning.value = false;
  }
};

const handleReprocessToggle = (value: boolean): void => {
  reprocessArmed.value = value === true;
  if (reprocessArmed.value && itemQrcodeRef.value) {
    itemQrcodeRef.value.closeCamera();
  }
};

const handleReprocessSubmit = (): void => {
  const itemQr = itemValue.value.trim();
  if (!itemQr) return;
  emit('reprocess-scan', itemQr);
};

const handleProductionRefresh = (): void => {
  if (itemValue.value.trim()) {
    doLookup(itemValue.value.trim());
  }
};

const handleClearItem = (): void => {
  itemValue.value = '';
  scannedItem.value = null;
  scannedSteps.value = [];
  selectedStepId.value = null;
  emit('update:modelValue', '');
};

const stepStatusLabel = (status: string): string => {
  const map: Record<string, string> = {
    pending: t('Pendente'),
    in_progress: t('Em andamento'),
    completed: t('Concluído'),
    skipped: t('Pulado'),
    reprocess_pending: t('Reprocesso pendente'),
  };
  return map[status] || status;
};

const disarmReprocess = (): void => {
  reprocessArmed.value = false;
};

watch(itemValue, (newValue: string) => {
  if (newValue && newValue.trim().length > 3 && !scannedItem.value) {
    // The handleItemUpdate already fires on each update, this is a fallback
    // for direct typing.
  }
});

onMounted(async () => {
  try {
    productionSteps.value = await fetchAll() as ProductionSteps_StepRow[];
    log('ScannerInputV2', 'onMounted', 'Production steps loaded', {
      count: productionSteps.value.length,
    });
  } catch {
    // Non-critical — QR code mapping will be partial
  }

  eventBus.on('reprocesso.created', disarmReprocess);
  eventBus.on('pdv.producao.refresh', handleProductionRefresh);
});

onBeforeUnmount(() => {
  if (lookupDebounceTimer) {
    clearTimeout(lookupDebounceTimer);
  }
  eventBus.off('reprocesso.created', disarmReprocess);
  eventBus.off('pdv.producao.refresh', handleProductionRefresh);
});
</script>

<style scoped>
.scanner-input-v2 {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.scanner-section {
  /* wrapper for step 1 */
}

.scanner-card {
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 12px;
  padding: 1rem;
}

.scanner-card-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.75rem;
}

.step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 999px;
  background: var(--p-primary-color, #2563eb);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  flex: 0 0 auto;
}

.scanner-label {
  font-size: 0.9rem;
  font-weight: 600;
}

.scanner-input-shell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 3.25rem;
  padding: 0.35rem 0.5rem;
  border: 1px solid var(--p-surface-300, #d1d5db);
  border-radius: 12px;
  background: var(--p-surface-50, #fafafa);
}

.scanner-input-shell :deep(.input-text-wrapper) {
  flex: 1;
  margin-bottom: 0;
}

.scanner-input-shell :deep(.input-field-container) {
  width: 100%;
}

.scanner-input-shell :deep(.input-qrcode-field) {
  border: none;
  box-shadow: none;
  background: transparent;
  padding-left: 0;
  padding-right: 3rem;
}

.scanner-indicator {
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 999px;
  background: var(--p-surface-300, #d1d5db);
  flex: 0 0 auto;
}

.scanner-indicator.active {
  background: var(--p-green-500, #22c55e);
}

/* Item info card */
.item-info-card {
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-primary-color, #2563eb);
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

.item-info-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.item-info-service {
  font-weight: 700;
  font-size: 1.05rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.item-info-type-badge {
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  background: var(--p-primary-color, #2563eb);
  color: #fff;
}

.item-info-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: 0.4rem 1.5rem;
}

.item-info-detail {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.detail-label {
  font-size: 0.75rem;
  color: var(--p-text-muted-color, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.detail-value {
  font-size: 0.9rem;
  font-weight: 500;
}

/* Steps card */
.steps-card {
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 12px;
  padding: 1rem;
}

.steps-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.steps-card-header h3 {
  margin: 0;
  font-size: 1rem;
}

.steps-progress {
  font-size: 0.85rem;
  color: var(--p-text-muted-color, #6b7280);
}

.steps-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 12px;
  cursor: default;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  background: var(--p-surface-0, #fff);
}

.step-item:hover {
  border-color: var(--p-surface-300, #d1d5db);
}

.step-item.step-next-expected {
  border-color: var(--p-primary-color, #2563eb);
  background: rgba(37, 99, 235, 0.04);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  cursor: pointer;
}

.step-item.step-next-expected:hover {
  border-color: var(--p-primary-500, #1d4ed8);
  background: rgba(37, 99, 235, 0.08);
}

.step-item.step-selected {
  border-color: var(--p-green-500, #22c55e);
  background: rgba(34, 197, 94, 0.06);
  cursor: pointer;
}

.step-item.step-selected:hover {
  background: rgba(34, 197, 94, 0.10);
}

.step-item.step-status-completed {
  opacity: 0.75;
}

.step-item.step-status-skipped {
  opacity: 0.6;
}

.step-item.step-status-in_progress {
  border-color: var(--p-blue-400, #60a5fa);
  background: rgba(59, 130, 246, 0.06);
}

.step-item.step-status-reprocess_pending {
  border-color: var(--p-orange-400, #fb923c);
  background: rgba(251, 146, 60, 0.06);
}

.step-status-icon {
  flex: 0 0 auto;
  width: 1.5rem;
  text-align: center;
  font-size: 1.1rem;
}

.step-status-icon .pi-check-circle {
  color: var(--p-green-500, #22c55e);
}

.step-status-icon .pi-ban {
  color: var(--p-red-400, #f87171);
}

.step-status-icon .pi-spinner {
  color: var(--p-blue-500, #3b82f6);
}

.step-status-icon .pi-exclamation-triangle {
  color: var(--p-orange-500, #f97316);
}

.step-status-icon .pi-circle {
  color: var(--p-surface-300, #d1d5db);
}

.step-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.step-topline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.step-name {
  font-weight: 600;
  font-size: 0.9rem;
}

.step-next-badge {
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  background: var(--p-primary-color, #2563eb);
  color: #fff;
  letter-spacing: 0.03em;
  animation: pulse-badge 2s ease-in-out infinite;
}

@keyframes pulse-badge {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.step-status-text {
  font-size: 0.78rem;
  color: var(--p-text-muted-color, #6b7280);
}

/* Actions */
.scanner-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: stretch;
}

.scanner-actions-secondary {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.scanner-reprocess-hint {
  color: var(--p-text-muted-color, #6b7280);
  font-size: 0.8rem;
}

/* Panel state (loading) */
.panel-state {
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  border: 1px dashed var(--p-surface-300, #d1d5db);
  border-radius: 12px;
  color: var(--p-text-muted-color, #6b7280);
  background: var(--p-surface-50, #fafafa);
  padding: 1rem;
}

/* Reprocess toggle — override InputToggle fixed label width to prevent overlap */
.scanner-reprocess-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.scanner-reprocess-row :deep(.toggle-label) {
  flex: 0 0 auto;
  min-width: 0;
  text-align: left;
}

.scanner-reprocess-row :deep(.toggle-container) {
  margin: 0;
}

.scanner-reprocess-row.is-over-camera {
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100000;
  max-width: calc(100% - 2rem);
  padding: 0.75rem 1rem;
  border-radius: 12px;
  background: var(--p-surface-0, #fff);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
}
</style>
